import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CircularProgress} from '@mui/material';
import { FiFileText, FiClock, FiCalendar } from 'react-icons/fi';
import { Eye, CheckCircle, Clock, AlertCircle, ChevronUp, ExternalLink, MessageSquare, XCircle, Loader2, Archive, FileText, RefreshCw, ClipboardList } from 'lucide-react';
import { formatDate } from '../../../global';
import TagEditor from './TagEditor';
import Timeline from '../../component/Timeline';
import { endInterview } from '../../../../api/client';
import DynamicBasicDialog from '../../component/DynamicBasicDialog';

const NO_SCORE = '—';

const SCORE_BADGE_CLASSES = {
    high: 'bg-emerald-100 text-emerald-800',
    good: 'bg-blue-100 text-blue-800',
    fair: 'bg-orange-100 text-orange-800',
    low: 'bg-red-100 text-red-800',
    pending: 'bg-yellow-100 text-yellow-800',
};

const SCORE_CARD_BADGE_CLASSES = {
    high: 'bg-emerald-100 text-emerald-800 border-2 border-emerald-200',
    good: 'bg-blue-100 text-blue-800 border-2 border-blue-200',
    fair: 'bg-orange-100 text-orange-800 border-2 border-orange-200',
    low: 'bg-red-100 text-red-800 border-2 border-red-200',
    pending: 'bg-yellow-100 text-yellow-800 border-2 border-yellow-200',
};

const getScoreTone = (score) => {
    if (score == null) return 'pending';
    if (score >= 80) return 'high';
    if (score >= 60) return 'good';
    if (score >= 40) return 'fair';
    return 'low';
};

// 'ready' once the report scores have landed, 'generating' while the backend is
// still working on it, 'unavailable' once the backend has given up. `report_status`
// is written onto the row by the report poller in CandidateDetails.
const getReportState = (session) => {
    if (session?.status !== 'completed') return 'none';
    if (session?.feedback?.detailed_breakdown?.effective_score != null) return 'ready';
    if (session?.report_status === 'failed' || session?.report_status === 'not_applicable') return 'unavailable';
    return 'generating';
};

// Sortable Header Component
const SortableHeader = ({ children, sortKey, currentSortKey, sortDirection, onSort }) => {
    const isActive = currentSortKey === sortKey;
    
    return (
        <th 
            className="text-left py-2 px-2 text-gray-900 cursor-pointer hover:bg-gray-100 transition-colors select-none"
            onClick={() => onSort && onSort(sortKey)}
        >
            <div className="flex items-center gap-1">
                {children}
                {onSort && (
                    isActive ? (
                        sortDirection === 'asc' ? 
                            <ChevronUp size={16} className="text-blue-600" /> : 
                            <ChevronUp size={16} className="text-blue-600 rotate-180" />
                    ) : (
                        <ChevronUp size={16} className="opacity-30" />
                    )
                )}
            </div>
        </th>
    );
};

const InterviewSessions = ({ 
    candidateData,
    setCandidateData,
    loading, 
    isCompanyAdmin,
    navigate,
    onViewResults,
    fetchPage,
    onDeleteSession,
    candidateId,
    onOpenComments,
    onViewFeedback
}) => {
    const { t } = useTranslation();
    // Timeline state
    const [timelineOpen, setTimelineOpen] = useState(false);
    const [selectedSessionId, setSelectedSessionId] = useState(null);
    // Show archived state
    const [showArchived, setShowArchived] = useState(false);
    // End interview state
    const [endingInterview, setEndingInterview] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState({ open: false, sessionId: null, sessionTitle: null });
    const [, setError] = useState(null);
    const getStatusConfig = (status) => {
        switch (status) {
            case 'completed':
                return {
                    icon: CheckCircle,
                    bgColor: 'bg-green-100',
                    textColor: 'text-green-800',
                    iconColor: 'text-green-600',
                    label: t('candidateDetails.interviewSessions.status.completed')
                };
            case 'in_progress':
                return {
                    icon: Clock,
                    bgColor: 'bg-blue-100',
                    textColor: 'text-blue-800',
                    iconColor: 'text-blue-600',
                    label: t('candidateDetails.interviewSessions.status.inProgress')
                };
            default:
                return {
                    icon: AlertCircle,
                    bgColor: 'bg-gray-100',
                    textColor: 'text-gray-800',
                    iconColor: 'text-gray-600',
                    label: status || t('candidateDetails.interviewSessions.status.unknown')
                };
        }
    };

    const formatScore = (score) => {
        if (score === null || score === undefined) return NO_SCORE;
        return `${parseFloat(score).toFixed(2)}%`;
    };

    const formatDuration = (minutes) => {
        if (!minutes) return '—';
        if (minutes < 60) return `${minutes}m`;
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
    };

    const getCompanyId = () => {
        // Try to get company_id from candidateData
        const companyId = candidateData?.company?.[0]?.id || candidateData?.company_id;
        if (companyId) return companyId;
        
        // Try to get from token
        try {
            const adminToken = localStorage.getItem('adminToken') || localStorage.getItem('token');
            if (adminToken) {
                const tokenPayload = JSON.parse(atob(adminToken.split('.')[1]));
                return tokenPayload.company_id;
            }
        } catch (err) {
            console.warn('Could not decode token for company_id');
        }
        
        // Try to get from user's admin_companies
        try {
            const userStr = localStorage.getItem('user') || localStorage.getItem('adminUser');
            if (userStr) {
                const user = JSON.parse(userStr);
                if (user?.admin_companies && user.admin_companies.length > 0) {
                    // Return first company - might need adjustment based on your data structure
                    return user.admin_companies[0];
                }
            }
        } catch (err) {
            console.warn('Could not get company_id from user');
        }
        
        return null;
    };

    const handleOpenFeedbackPage = (sessionId) => {
        const companyId = getCompanyId();
        if (!companyId || !candidateId || !sessionId) {
            console.error('Missing required IDs for feedback page');
            return;
        }
        const url = `/feedbacks/${companyId}/${candidateId}/${sessionId}`;
        window.open(url, '_blank');
    };

    const handleTagsUpdate = (sessionId, newTags) => {
        setCandidateData(prev => {
            if (!prev.interviews) {
                return prev;
            }
            
            // Handle paginated response (object with results array)
            if (prev.interviews.results && Array.isArray(prev.interviews.results)) {
                return {
                    ...prev,
                    interviews: {
                        ...prev.interviews,
                        results: prev.interviews.results.map(s => 
                            s.id === sessionId 
                                ? { ...s, tags: newTags }
                                : s
                        )
                    }
                };
            }
            
            // Handle array response
            if (Array.isArray(prev.interviews)) {
                return {
                    ...prev,
                    interviews: prev.interviews.map(s => 
                        s.id === sessionId 
                            ? { ...s, tags: newTags }
                            : s
                    )
                };
            }
            
            return prev;
        });
    };

    const handleEndInterviewClick = (sessionId, sessionTitle) => {
        setConfirmDialog({
            open: true,
            sessionId: sessionId,
            sessionTitle: sessionTitle
        });
        setError(null);
    };

    const handleConfirmEndInterview = async () => {
        if (!confirmDialog.sessionId) return;

        setEndingInterview(confirmDialog.sessionId);
        setConfirmDialog({ open: false, sessionId: null, sessionTitle: null });
        setError(null);

        try {
            await endInterview(confirmDialog.sessionId);
            
            // Refresh the current page
            if (fetchPage && candidateData?.interviews?.current_page) {
                await fetchPage(candidateData.interviews.current_page);
            }
        } catch (err) {
            console.error('Error ending interview:', err);
            setError(err.response?.data?.message || err.message || 'Failed to end interview. Please try again.');
            setEndingInterview(null);
        }
    };

    const handleCancelEndInterview = () => {
        setConfirmDialog({ open: false, sessionId: null, sessionTitle: null });
        setError(null);
    };

    const rawInterviews = candidateData?.interviews;
    
    const candidateSessions = Array.isArray(rawInterviews) 
        ? rawInterviews 
        : (rawInterviews?.results || []);

    const renderLoadingState = () => (
        <div className="text-center py-12">
            <CircularProgress size={40} />
            <p className="mt-4 text-gray-500">
                {t('candidateDetails.interviewSessions.loading')}
            </p>
        </div>
    );

    const renderDesktopTable = () => (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full bg-white">
                <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                        <SortableHeader>
                            <span className="text-sm font-medium">{t('candidateDetails.interviewSessions.tableHeaders.interviewId')}</span>
                        </SortableHeader>
                        <SortableHeader>
                            <span className="text-sm font-medium">{t('candidateDetails.interviewSessions.tableHeaders.position')}</span>
                        </SortableHeader>
                        <SortableHeader>
                            <span className="text-sm font-medium">{t('candidateDetails.interviewSessions.tableHeaders.dateTime')}</span>
                        </SortableHeader>
                        <SortableHeader>
                            <span className="text-sm font-medium">{t('candidateDetails.interviewSessions.tableHeaders.effectiveScore')}</span>
                        </SortableHeader>
                        <SortableHeader>
                            <span className="text-sm font-medium">{t('candidateDetails.interviewSessions.tableHeaders.totalScore')}</span>
                        </SortableHeader>
                        <SortableHeader>
                            <span className="text-sm font-medium">{t('candidateDetails.interviewSessions.tableHeaders.tags')}</span>
                        </SortableHeader>
                        <th className="text-center py-2 px-2 text-sm font-medium text-gray-900">{t('candidateDetails.interviewSessions.tableHeaders.actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {candidateSessions.map((session, index) => {
                        const statusConfig = getStatusConfig(session.status);
                        const StatusIcon = statusConfig.icon;
                        const isReportGenerating = getReportState(session) === 'generating';
                        const effectiveScore = session?.feedback?.detailed_breakdown?.effective_score;
                        const totalScore = session?.feedback?.detailed_breakdown?.total_score;

                        return (
                            <tr 
                                key={session.id}
                                className={`border-b border-gray-100 hover:bg-blue-50 transition-colors ${
                                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                                }`}
                            >
                                {/* Interview ID & Status */}
                                <td className="py-3 px-2">
                                    <div className="flex flex-col gap-1">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <span className="text-gray-900 text-sm font-medium">
                                                #{session.id}
                                            </span>
                                            {session.is_archived && (
                                                <span className="px-1.5 py-0.5 rounded-full border bg-orange-100 text-orange-800 border-orange-200 flex items-center gap-1 text-[10px] font-medium">
                                                    <Archive size={9} />
                                                    {t('interviewOverview.archived')}
                                                </span>
                                            )}
                                        </div>
                                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm w-fit ${statusConfig.bgColor} ${statusConfig.textColor}`}>
                                            <StatusIcon size={10} className={statusConfig.iconColor} />
                                            {statusConfig.label}
                                        </span>
                                    </div>
                                </td>
                                {/* Position */}
                                <td className="py-3 px-2 max-w-[150px]">
                                    <span className="text-sm text-gray-700 truncate block">
                                        {session?.job_description?.title || 'N/A'}
                                    </span>
                                    {session.candidate_has_jd_tag && session?.job_description?.id && (
                                        <Link
                                            to={`/job-descriptions?jd=${session?.job_description?.id}`}
                                            className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium mt-1"
                                            title={t('candidateDetails.jobDescriptionsTooltip')}
                                        >
                                            <FileText size={10} />
                                            {t('candidateDetails.viewJdContext')}
                                        </Link>
                                    )}
                                </td>

                                {/* Date & Time */}
                                <td className="py-3 px-2">
                                    <span className="text-gray-900 text-sm">
                                        {session.started_at ? formatDate(session.started_at) : t('candidateDetails.nA')}
                                    </span>
                                </td>

                                {/* Effective Score */}
                                <td className="py-3 px-2">
                                    {isReportGenerating ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                                            <Loader2 size={12} className="animate-spin" />
                                            {t('candidateDetails.interviewSessions.generatingReport', 'Generating report…')}
                                        </span>
                                    ) : (
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-medium ${SCORE_BADGE_CLASSES[getScoreTone(effectiveScore)]}`}>
                                            {formatScore(effectiveScore)}
                                        </span>
                                    )}
                                </td>
                                {/* Total Score */}
                                <td className="py-3 px-2">
                                    <span className="text-sm text-gray-700 font-medium">
                                        {isReportGenerating ? NO_SCORE : formatScore(totalScore)}
                                    </span>
                                </td>

                                {/* Tags */}
                                <td className="py-3 px-2 max-w-[150px]">
                                    <TagEditor 
                                        tags={session.tags} 
                                        sessionId={session.id}
                                        onTagsUpdate={(newTags) => handleTagsUpdate(session.id, newTags)}
                                        isCompanyAdmin={isCompanyAdmin}
                                        maxTags={2}
                                    />
                                </td>

                                {/* Actions */}
                                <td className="py-3 px-2">
                                    <div className="flex justify-center items-center gap-1">
                                        {session.status === 'in_progress' && (
                                            <button
                                                onClick={() => handleEndInterviewClick(session.id, session?.job_description?.title || `Interview #${session.id}`)}
                                                disabled={endingInterview === session.id}
                                                className="p-1 text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                title="End Interview"
                                            >
                                                {endingInterview === session.id ? (
                                                    <Loader2 size={12} className="animate-spin" />
                                                ) : (
                                                    <XCircle size={12} />
                                                )}
                                            </button>
                                        )}
                                        {session.status === 'completed' && (
                                            <>
                                                <button
                                                    onClick={() => onViewResults(session.id)}
                                                    disabled={isReportGenerating}
                                                    className="flex items-center gap-1 px-2 py-1 rounded-md transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                                    title={isReportGenerating
                                                        ? t('candidateDetails.interviewSessions.reportGenerating', 'Report is still being generated')
                                                        : t('candidateDetails.interviewSessions.viewResults')}
                                                    style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)', color: 'white'}}
                                                >
                                                    {isReportGenerating ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />}
                                                    View
                                                </button>
                                                
                                                <button
                                                    onClick={() => {
                                                        setSelectedSessionId(session.id);
                                                        setTimelineOpen(true);
                                                    }}
                                                    className="p-1 text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                                                    title={t('candidateDetails.interviewSessions.viewTimeline')}
                                                >
                                                    <Clock size={12} />
                                                </button>
                                                <button
                                                    onClick={() => handleOpenFeedbackPage(session.id)}
                                                    className="p-1 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                                                    title={t('candidateDetails.interviewSessions.openFeedbackPage', 'Open feedback page in new tab')}
                                                >
                                                    <ExternalLink size={12} />
                                                </button>
                                            </>
                                        )}
                                        {/* Post-Interview Feedback button */}
                                        {session.status === 'completed' && onViewFeedback && (
                                            <button
                                                onClick={() => onViewFeedback(session.id, session)}
                                                className="p-1 text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors"
                                                title={t('interviewOverview.viewFeedback', 'Post-Interview Feedback')}
                                            >
                                                <ClipboardList size={12} />
                                            </button>
                                        )}
                                        {/* Comments button - available for all sessions */}
                                        <button
                                            onClick={() => {
                                                if (onOpenComments) {
                                                    onOpenComments(session.id, session);
                                                }
                                            }}
                                            className="p-1 text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                                            title={t('comments.title', 'Comments')}
                                        >
                                            <MessageSquare size={12} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );

    const renderMobileCards = () => (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {candidateSessions.map((session) => {
                const statusConfig = getStatusConfig(session.status);
                const StatusIcon = statusConfig.icon;
                const isReportGenerating = getReportState(session) === 'generating';
                const effectiveScore = session?.feedback?.detailed_breakdown?.effective_score;

                return (
                    <div
                        key={session.id}
                        className="bg-white rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all duration-200 p-4"
                    >
                        {/* Header */}
                        <div className="flex justify-between items-center mb-3">
                            <div className="flex items-center gap-1.5 flex-wrap">
                                <h3 className="text-gray-900">
                                    {t('candidateDetails.interviewSessions.interviewId', { id: session.id })}
                                </h3>
                                {session.is_archived && (
                                    <span className="px-1.5 py-0.5 rounded-full border bg-orange-100 text-orange-800 border-orange-200 flex items-center gap-1 text-[10px] font-medium">
                                        <Archive size={9} />
                                        {t('interviewOverview.archived')}
                                    </span>
                                )}
                            </div>
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full  ${statusConfig.bgColor} ${statusConfig.textColor}`}>
                                <StatusIcon size={12} className={statusConfig.iconColor} />
                                {statusConfig.label}
                            </span>
                        </div>

                        {/* Details */}
                        <div className="space-y-2 mb-4">
                            <div className="flex items-center gap-2 ">
                                <FiCalendar size={14} className="text-gray-500 flex-shrink-0" />
                                <span className="text-gray-700">
                                    {session.started_at ? formatDate(session.started_at) : t('candidateDetails.nA')}
                                </span>
                            </div>
                            <div className="flex items-center gap-2 ">
                                <FiClock size={14} className="text-gray-500 flex-shrink-0" />
                                <span className="text-gray-700">
                                    {formatDuration(session.duration_minutes)}
                                </span>
                            </div>
                            <div className="flex items-center gap-2 ">
                                <FiClock size={14} className="text-gray-500 flex-shrink-0" />
                                <span className="text-gray-700">
                                    {session.started_at ? new Date(session.started_at).toLocaleTimeString() : t('candidateDetails.nA')}
                                </span>
                            </div>
                            {session.candidate_has_jd_tag && session?.job_description?.id && (
                                <div className="flex items-center gap-2">
                                    <Link
                                        to={`/job-descriptions?jd=${session?.job_description?.id}`}
                                        className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 font-medium text-sm"
                                        title={t('candidateDetails.jobDescriptionsTooltip')}
                                    >
                                        <FileText size={14} />
                                        {t('candidateDetails.viewJdContext')}
                                    </Link>
                                </div>
                            )}
                        </div>

                        {/* Score */}
                        <div className="mb-4 p-3 bg-gray-50 rounded-lg border text-center">
                            <p className=" text-gray-600 mb-1">{t('candidateDetails.interviewSessions.score')}:</p>
                            {isReportGenerating ? (
                                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-yellow-100 text-yellow-800 border-2 border-yellow-200">
                                    <Loader2 size={14} className="animate-spin" />
                                    {t('candidateDetails.interviewSessions.generatingReport', 'Generating report…')}
                                </span>
                            ) : (
                                <span className={`inline-flex items-center px-3 py-1 rounded-full ${SCORE_CARD_BADGE_CLASSES[getScoreTone(effectiveScore)]}`}>
                                    {formatScore(effectiveScore)}
                                </span>
                            )}
                        </div>

                        {/* Tags */}
                        <div className="mb-4">
                            <TagEditor 
                                tags={session.tags} 
                                sessionId={session.id}
                                onTagsUpdate={(newTags) => handleTagsUpdate(session.id, newTags)}
                                isCompanyAdmin={isCompanyAdmin}
                                maxTags={3}
                            />
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2">
                            {session.status === 'in_progress' && (
                                <>
                                    <button
                                        onClick={() => navigate(`/interview/${session.id}`)}
                                        className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white  rounded-lg transition-colors"
                                    >
                                        <Clock size={14} />
                                        {t('candidateDetails.interviewSessions.continueInterview')}
                                    </button>
                                    <button
                                        onClick={() => handleEndInterviewClick(session.id, session?.job_description?.title || `Interview #${session.id}`)}
                                        disabled={endingInterview === session.id}
                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-red-200"
                                        title="End Interview"
                                    >
                                        {endingInterview === session.id ? (
                                            <Loader2 size={14} className="animate-spin" />
                                        ) : (
                                            <XCircle size={14} />
                                        )}
                                    </button>
                                </>
                            )}
                            {session.status === 'completed' && (
                                <>
                                    <button
                                        onClick={() => onViewResults(session.id)}
                                        disabled={isReportGenerating}
                                        className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        title={isReportGenerating
                                            ? t('candidateDetails.interviewSessions.reportGenerating', 'Report is still being generated')
                                            : t('candidateDetails.interviewSessions.viewResults')}
                                    >
                                        {isReportGenerating ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                                        {t('candidateDetails.interviewSessions.viewResults')}
                                    </button>
                                    
                                    <button
                                        onClick={() => {
                                            setSelectedSessionId(session.id);
                                            setTimelineOpen(true);
                                        }}
                                        className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-full transition-colors"
                                        title={t('candidateDetails.interviewSessions.viewTimeline')}
                                        style={{ 
                                            width: '32px', 
                                            height: '32px', 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center' 
                                        }}
                                    >
                                        <Clock size={14} />
                                    </button>
                                    <button
                                        onClick={() => handleOpenFeedbackPage(session.id)}
                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                                        title={t('candidateDetails.interviewSessions.openFeedbackPage', 'Open feedback page in new tab')}
                                        style={{ 
                                            width: '32px', 
                                            height: '32px', 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center' 
                                        }}
                                    >
                                        <ExternalLink size={14} />
                                    </button>
                                </>
                            )}
                            {/* Post-Interview Feedback button */}
                            {session.status === 'completed' && onViewFeedback && (
                                <button
                                    onClick={() => onViewFeedback(session.id, session)}
                                    className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-full transition-colors"
                                    title={t('interviewOverview.viewFeedback', 'Post-Interview Feedback')}
                                    style={{ 
                                        width: '32px', 
                                        height: '32px', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center' 
                                    }}
                                >
                                    <ClipboardList size={14} />
                                </button>
                            )}
                            {/* Comments button - available for all sessions */}
                            <button
                                onClick={() => {
                                    if (onOpenComments) {
                                        onOpenComments(session.id, session);
                                    }
                                }}
                                className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-full transition-colors"
                                title={t('comments.title', 'Comments')}
                                style={{ 
                                    width: '32px', 
                                    height: '32px', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center' 
                                }}
                            >
                                <MessageSquare size={14} />
                            </button>
                            {/* <Tooltip title="Archive Session">
                                <button
                                    onClick={() => onDeleteSession(session.id)}
                                    className="p-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors border border-red-200 hover:border-red-300"
                                >
                                    <IoArchiveOutline />
                                </button>
                            </Tooltip> */}
                        </div>
                    </div>
                );
            })}
        </div>
    );

    const handleToggleArchived = () => {
        const newShowArchived = !showArchived;
        setShowArchived(newShowArchived);
        // Refetch page 1 with the new show_archived parameter (reset to first page when toggling)
        if (fetchPage) {
            fetchPage(1, newShowArchived);
        }
    };

    const handleRefreshSessions = () => {
        if (!fetchPage) return;
        const currentPage = candidateData?.interviews?.current_page || 1;
        fetchPage(currentPage, showArchived);
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                        <FiFileText className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">{t('candidateDetails.interviewSessions.title')}</h3>
                </div>
                <div className="flex items-center gap-1">
                    <button
                        onClick={handleRefreshSessions}
                        disabled={loading}
                        className={`p-1 rounded border transition-all ${
                            loading
                                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200'
                        }`}
                        title={t('common.refresh', 'Refresh')}
                    >
                        <RefreshCw size={10} className={loading ? 'animate-spin' : ''} />
                    </button>
                    <button
                        onClick={handleToggleArchived}
                        className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition-all flex items-center gap-0.5 ${
                            showArchived
                                ? 'bg-orange-100 text-orange-800 border border-orange-200'
                                : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                        }`}
                        title={showArchived ? t('interviewOverview.hideArchived', 'Hide archived') : t('interviewOverview.showArchived', 'Show archived')}
                    >
                        <Archive size={9} />
                        <span>{t('interviewOverview.archived')}</span>
                    </button>
                </div>
            </div>

            {loading ? renderLoadingState() : (
                candidateSessions.length === 0 ? (
                    <div className="text-center py-12">
                        <FiFileText size={48} className="mx-auto mb-4 text-gray-400" />
                        <h3 className="text-lg text-gray-500 mb-2">
                            {t('candidateDetails.interviewSessions.noSessions')}
                        </h3>
                        <p className="text-gray-400">
                            {t('candidateDetails.interviewSessions.noSessionsDescription')}
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Desktop Table View */}
                        <div className="hidden md:block">
                            {renderDesktopTable()}
                        </div>
                        
                        {/* Mobile Card View */}
                        <div className="md:hidden">
                            {renderMobileCards()}
                        </div>
                        {candidateData?.interviews?.current_page && (
                        <div className="flex items-center justify-end border-t border-gray-200 mt-6 pt-4">
                            {/* Previous Button */}
                            <button
                                onClick={() => fetchPage(candidateData.interviews.current_page - 1, showArchived)}
                                disabled={candidateData.interviews.current_page === 1}
                                className={`flex items-center px-4 py-2 text-sm font-medium rounded-md border 
                                    ${candidateData.interviews.current_page === 1
                                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                    }`}
                            >
                                {t('candidateDetails.interviewSessions.previous')}
                            </button>

                            {/* Page Info Text */}
                            <span className="text-sm px-4 text-gray-700">
                                {t('candidateDetails.interviewSessions.pageInfo', { 
                                    current: candidateData.interviews.current_page, 
                                    total: candidateData.interviews.num_pages 
                                })}
                            </span>

                            {/* Next Button */}
                            <button
                                onClick={() => fetchPage(candidateData.interviews.current_page + 1, showArchived)}
                                disabled={candidateData.interviews.current_page >= candidateData.interviews.num_pages}
                                className={`flex items-center px-4 py-2 text-sm font-medium rounded-md border 
                                    ${candidateData.interviews.current_page >= candidateData.interviews.num_pages
                                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                    }`}
                            >
                                {t('candidateDetails.interviewSessions.next')}
                            </button>
                        </div>
                    )}
                    </>
                )
            )}

            {/* Timeline Drawer */}
            {selectedSessionId && (
                <Timeline
                    open={timelineOpen}
                    onClose={() => {
                        setTimelineOpen(false);
                        setSelectedSessionId(null);
                    }}
                    apiEndpoint={`audit/timeline/interview/${selectedSessionId}/`}
                    title={t('candidateDetails.interviewSessions.timelineTitle', { id: selectedSessionId })}
                    pageName={t('candidateDetails.interviewSessions.title')}
                    width={450}
                />
            )}

            {/* End Interview Confirmation Dialog */}
            <DynamicBasicDialog
                open={confirmDialog.open}
                onClose={handleCancelEndInterview}
                onConfirm={handleConfirmEndInterview}
                title="End Interview Confirmation"
                content={
                    confirmDialog.sessionTitle
                        ? `Are you sure you want to end the interview for "${confirmDialog.sessionTitle}"? This will remove the Redis TTL, mark the interview as completed, and generate final feedback. This action cannot be undone.`
                        : `Are you sure you want to end this interview? This will remove the Redis TTL, mark the interview as completed, and generate final feedback. This action cannot be undone.`
                }
            />
        </div>
    );
};

export default InterviewSessions;